import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Divider, Modal, Paper, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import BackupIcon from '@mui/icons-material/Backup';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import BugReportIcon from '@mui/icons-material/BugReport';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CodeIcon from '@mui/icons-material/Code';
import WebhookIcon from '@mui/icons-material/Webhook';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DevicesIcon from '@mui/icons-material/Devices';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchServices, controlService } from '../api/services';
import { fetchGames, controlGame } from '../api/games';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../lib/permissionUtils';
import { useSnackbar } from '../context/SnackbarContext';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PaletteItem {
  id: string;
  group: string;
  label: string;
  subtitle?: string;
  icon: React.ReactElement;
  destructive?: boolean;
  execute: () => void | Promise<void>;
}

// ── Static nav items ───────────────────────────────────────────────────────────

interface NavDef { id: string; label: string; icon: React.ReactElement; path: string }

const NAV_DEFS: NavDef[] = [
  { id: 'nav-dashboard',      label: 'Dashboard',      icon: <DashboardIcon fontSize="small" />,          path: '/' },
  { id: 'nav-services',       label: 'Services',       icon: <SettingsApplicationsIcon fontSize="small" />, path: '/services' },
  { id: 'nav-games',          label: 'Games',          icon: <SportsEsportsIcon fontSize="small" />,       path: '/games' },
  { id: 'nav-backups',        label: 'Backups',        icon: <BackupIcon fontSize="small" />,              path: '/backups' },
  { id: 'nav-firewall',       label: 'Firewall',       icon: <SecurityIcon fontSize="small" />,            path: '/firewall' },
  { id: 'nav-webserver',      label: 'Webserver',      icon: <LanguageIcon fontSize="small" />,            path: '/webserver' },
  { id: 'nav-mysql',          label: 'MySQL',          icon: <StorageIcon fontSize="small" />,             path: '/mysql' },
  { id: 'nav-errors',         label: 'Error Log',      icon: <BugReportIcon fontSize="small" />,           path: '/errors' },
  { id: 'nav-about',          label: 'About',          icon: <InfoIcon fontSize="small" />,                path: '/about' },
  { id: 'nav-users',          label: 'Users',          icon: <PeopleIcon fontSize="small" />,              path: '/users' },
  { id: 'nav-security',       label: 'Security',       icon: <GppMaybeIcon fontSize="small" />,            path: '/security' },
  { id: 'nav-security-audit', label: 'Security Audit', icon: <VerifiedUserIcon fontSize="small" />,        path: '/security-audit' },
  { id: 'nav-wishlist',       label: 'Wishlist',       icon: <BookmarkIcon fontSize="small" />,            path: '/wishlist' },
  { id: 'nav-development',    label: 'Development',    icon: <CodeIcon fontSize="small" />,                path: '/development' },
  { id: 'nav-discord',        label: 'Discord',        icon: <WebhookIcon fontSize="small" />,             path: '/discord' },
  { id: 'nav-cron',           label: 'Cron Jobs',      icon: <ScheduleIcon fontSize="small" />,            path: '/cron' },
  { id: 'nav-sessions',       label: 'Sessions',       icon: <DevicesIcon fontSize="small" />,             path: '/sessions' },
  { id: 'nav-claude',         label: 'Claude',         icon: <SmartToyIcon fontSize="small" />,            path: '/claude' },
  { id: 'nav-audit',          label: 'Audit Log',      icon: <HistoryIcon fontSize="small" />,             path: '/audit' },
];

// ── Recent history (localStorage) ─────────────────────────────────────────────

const recentKey = (u: string) => `cp_recent_${u}`;
const MAX_RECENT = 5;

function getRecent(username: string): string[] {
  try { return JSON.parse(localStorage.getItem(recentKey(username)) ?? '[]') as string[]; }
  catch { return []; }
}

function pushRecent(username: string, id: string) {
  const prev = getRecent(username).filter((x) => x !== id);
  localStorage.setItem(recentKey(username), JSON.stringify([id, ...prev].slice(0, MAX_RECENT)));
}

// ── Component ──────────────────────────────────────────────────────────────────

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { auth } = useAuth();

  const username = auth.status === 'authenticated' ? auth.username : '';
  const permissions = auth.status === 'authenticated' ? auth.permissions : [];
  const canServices = hasPermission(permissions, 'services.manage');

  const { data: servicesData } = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    enabled: open,
    staleTime: 30_000,
  });

  const { data: gamesData } = useQuery({
    queryKey: ['games'],
    queryFn: fetchGames,
    enabled: open,
    staleTime: 30_000,
  });

  const handleClose = useCallback(() => { setOpen(false); setConfirming(null); }, []);

  // Ctrl/Cmd+K global shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((v) => {
          if (v) return false;
          setQuery('');
          setSelectedIdx(0);
          setConfirming(null);
          return true;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Build full item list
  const allItems = useMemo((): PaletteItem[] => {
    const items: PaletteItem[] = NAV_DEFS.map((n) => ({
      id: n.id,
      group: 'Navigation',
      label: n.label,
      icon: n.icon,
      execute: () => { navigate(n.path); handleClose(); },
    }));

    if (canServices && servicesData) {
      for (const svc of servicesData.services) {
        if (svc.active !== 'active') {
          items.push({
            id: `svc-start-${svc.id}`,
            group: 'Actions',
            label: `Start ${svc.name}`,
            subtitle: 'service',
            icon: <PlayArrowIcon fontSize="small" />,
            execute: async () => {
              setRunning(`svc-start-${svc.id}`);
              try { await controlService(svc.id, 'start'); showSnackbar(`Started ${svc.name}`, 'success'); }
              catch { showSnackbar(`Failed to start ${svc.name}`, 'error'); }
              finally { setRunning(null); }
              handleClose();
            },
          });
        }
        if (svc.active === 'active') {
          items.push({
            id: `svc-stop-${svc.id}`,
            group: 'Actions',
            label: `Stop ${svc.name}`,
            subtitle: 'service',
            icon: <StopIcon fontSize="small" />,
            destructive: true,
            execute: async () => {
              setRunning(`svc-stop-${svc.id}`);
              try { await controlService(svc.id, 'stop'); showSnackbar(`Stopped ${svc.name}`, 'success'); }
              catch { showSnackbar(`Failed to stop ${svc.name}`, 'error'); }
              finally { setRunning(null); }
              handleClose();
            },
          });
        }
        items.push({
          id: `svc-restart-${svc.id}`,
          group: 'Actions',
          label: `Restart ${svc.name}`,
          subtitle: 'service',
          icon: <RestartAltIcon fontSize="small" />,
          destructive: true,
          execute: async () => {
            setRunning(`svc-restart-${svc.id}`);
            try { await controlService(svc.id, 'restart'); showSnackbar(`Restarted ${svc.name}`, 'success'); }
            catch { showSnackbar(`Failed to restart ${svc.name}`, 'error'); }
            finally { setRunning(null); }
            handleClose();
          },
        });
      }
    }

    if (gamesData) {
      for (const game of gamesData.games) {
        if (!hasPermission(permissions, `games.${game.id}.control`)) continue;
        if (game.status.active !== 'active') {
          items.push({
            id: `game-start-${game.id}`,
            group: 'Actions',
            label: `Start ${game.name}`,
            subtitle: 'game',
            icon: <PlayArrowIcon fontSize="small" />,
            execute: async () => {
              setRunning(`game-start-${game.id}`);
              try { await controlGame(game.id, 'start'); showSnackbar(`Started ${game.name}`, 'success'); }
              catch { showSnackbar(`Failed to start ${game.name}`, 'error'); }
              finally { setRunning(null); }
              handleClose();
            },
          });
        }
        if (game.status.active === 'active') {
          items.push({
            id: `game-stop-${game.id}`,
            group: 'Actions',
            label: `Stop ${game.name}`,
            subtitle: 'game',
            icon: <StopIcon fontSize="small" />,
            destructive: true,
            execute: async () => {
              setRunning(`game-stop-${game.id}`);
              try { await controlGame(game.id, 'stop'); showSnackbar(`Stopped ${game.name}`, 'success'); }
              catch { showSnackbar(`Failed to stop ${game.name}`, 'error'); }
              finally { setRunning(null); }
              handleClose();
            },
          });
        }
        items.push({
          id: `game-restart-${game.id}`,
          group: 'Actions',
          label: `Restart ${game.name}`,
          subtitle: 'game',
          icon: <RestartAltIcon fontSize="small" />,
          destructive: true,
          execute: async () => {
            setRunning(`game-restart-${game.id}`);
            try { await controlGame(game.id, 'restart'); showSnackbar(`Restarted ${game.name}`, 'success'); }
            catch { showSnackbar(`Failed to restart ${game.name}`, 'error'); }
            finally { setRunning(null); }
            handleClose();
          },
        });
      }
    }

    return items;
  }, [navigate, handleClose, canServices, servicesData, gamesData, permissions, showSnackbar]);

  // Filter + prepend recent when no query
  const filteredItems = useMemo((): PaletteItem[] => {
    const q = query.toLowerCase().trim();
    if (!q) {
      const recentIds = getRecent(username);
      if (recentIds.length > 0) {
        const recentItems = recentIds
          .map((id) => allItems.find((x) => x.id === id))
          .filter((x): x is PaletteItem => !!x)
          .map((x) => ({ ...x, group: 'Recent' }));
        const recentSet = new Set(recentIds);
        return [...recentItems, ...allItems.filter((x) => !recentSet.has(x.id))];
      }
      return allItems;
    }
    return allItems.filter(
      (x) => x.label.toLowerCase().includes(q) || (x.subtitle ?? '').toLowerCase().includes(q),
    );
  }, [query, allItems, username]);

  // Reset selection when list changes
  useEffect(() => { setSelectedIdx(0); setConfirming(null); }, [filteredItems]);

  // Scroll selected item into view
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${selectedIdx}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const executeItem = useCallback(async (item: PaletteItem) => {
    if (item.destructive && confirming !== item.id) {
      setConfirming(item.id);
      return;
    }
    setConfirming(null);
    pushRecent(username, item.id);
    await item.execute();
  }, [confirming, username]);

  const onInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (confirming) { setConfirming(null); } else { handleClose(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, filteredItems.length - 1));
      setConfirming(null);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
      setConfirming(null);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = filteredItems[selectedIdx];
      if (item) void executeItem(item);
    }
  }, [confirming, handleClose, filteredItems, selectedIdx, executeItem]);

  // Group items for display
  const grouped = useMemo(() => {
    const map = new Map<string, { item: PaletteItem; idx: number }[]>();
    filteredItems.forEach((item, idx) => {
      if (!map.has(item.group)) map.set(item.group, []);
      map.get(item.group)!.push({ item, idx });
    });
    return [...map.entries()];
  }, [filteredItems]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.65)' } } }}
      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: '10vh' }}
    >
      <Paper
        elevation={24}
        sx={{ width: '100%', maxWidth: 640, mx: 2, borderRadius: 2, overflow: 'hidden', outline: 'none' }}
      >
        {/* Search bar */}
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search commands…"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '1rem',
              color: 'inherit',
              fontFamily: 'inherit',
            }}
          />
          <Chip label="Esc" size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 20, flexShrink: 0 }} />
        </Box>

        {/* Results list */}
        <Box ref={listRef} sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {filteredItems.length === 0 && (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No commands found</Typography>
            </Box>
          )}
          {grouped.map(([group, entries], gi) => (
            <Box key={group}>
              <Box sx={{ px: 2, pt: gi === 0 ? 1 : 0.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.62rem', fontWeight: 700, flexShrink: 0 }}>
                  {group}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
              {entries.map(({ item, idx }) => {
                const sel = idx === selectedIdx;
                const conf = confirming === item.id;
                const busy = running === item.id;
                return (
                  <Box
                    key={item.id}
                    data-idx={idx}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    onClick={() => void executeItem(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 0.85,
                      cursor: 'pointer',
                      bgcolor: sel ? (conf ? 'rgba(237,108,2,0.12)' : 'rgba(255,255,255,0.06)') : 'transparent',
                      borderLeft: '2px solid',
                      borderLeftColor: sel ? (conf ? 'warning.main' : 'primary.main') : 'transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                    }}
                  >
                    <Box sx={{ color: item.destructive ? (conf ? 'warning.main' : 'error.light') : 'text.secondary', display: 'flex', flexShrink: 0 }}>
                      {busy ? <CircularProgress size={16} /> : item.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: sel ? 600 : 400, color: conf ? 'warning.light' : 'text.primary' }}>
                        {item.label}
                      </Typography>
                      {item.subtitle && (
                        <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block', fontSize: '0.68rem' }}>
                          {item.subtitle}
                        </Typography>
                      )}
                    </Box>
                    {conf && (
                      <Chip label="Enter to confirm" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.62rem', height: 20 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Footer hints */}
        <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 2 }}>
          {(['↑↓ navigate', '↵ select', 'Esc close'] as const).map((hint) => {
            const [key, label] = hint.split(' ');
            return (
              <Box key={hint} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip label={key} size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 18, fontFamily: 'monospace' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Modal>
  );
}
